import { token } from '@bp-files/ui';
import { PdfPager } from './PdfPager';
import { PdfSignTools, PdfUndoButton } from './PdfSignTools';
import { SIGNING_COPY } from './Signing/signing.copy';
import type { PdfControls } from './pdfControls';

/**
 * The phone's bottom bar: where you are, and the two things you do here.
 *
 * ```
 * ‹ 1 of 6 ›   Text   Sign
 * ```
 *
 * **The bar stays at the bottom on a phone and nowhere else.** Desktop folded
 * everything into one top toolbar because a band across the bottom is page the
 * screen is not showing; a phone has neither the horizontal room to carry
 * paging and signing up top nor a thumb that reaches there. So the rule is not
 * "one bar" — it is *controls where the pointer is*.
 *
 * **Sign is the prominent action**, by being the only white chip in a dark bar.
 * That treatment is not applied for prominence — `PdfSignTools` explains that
 * the button holds a mark drawn in `bp-ink` and has to sit on white to be
 * legible at all — it is simply what makes it the obvious thing here too.
 *
 * **Contextual once a tool is armed or a field is placed:**
 *
 * ```
 * Cancel                        Save signed copy
 * ```
 *
 * There is no room to carry paging, both tools *and* a save at thumb size, and
 * a reader mid-placement is not paging. Cancel is the way out — on desktop that
 * job is done by pressing the armed tool again, which that bar has room to keep
 * showing. `useSigning.discard` confirms first if anything was actually placed.
 *
 * **Both states are `min-h-16`.** A bar that changed height on arming would
 * resize the stage under the reader's thumb mid-gesture, re-lay-out the canvas,
 * and stale any measurement taken of it.
 */
export function PdfBottomBar({ controls }: { controls: PdfControls }) {
  const { signing, canSign } = controls;
  // The pencil stays armed across strokes, so `tool !== undefined` keeps the bar
  // contextual for the whole of a drawing, as it does for an armed stamp.
  // `canUndo` keeps it contextual too: a run of deletes can empty the document
  // while leaving edits to take back, and Undo lives only in this state.
  const placing = canSign && (signing.tool !== undefined || signing.hasContent || signing.canUndo);

  if (placing) {
    return (
      <div className="flex min-h-16 w-full items-center justify-between gap-3">
        <span className="flex items-center gap-2">
          <button
            type="button"
            disabled={signing.saving}
            onClick={signing.discard}
            // The name starts with the visible word (WCAG 2.5.3) and then says
            // what it costs, which "Cancel" alone does not.
            aria-label={SIGNING_COPY.discard.label}
            className="min-h-11 rounded-md border px-4 text-sm font-medium transition-colors disabled:opacity-60"
            style={{ borderColor: token('bp-border-strong'), color: token('bp-text') }}
          >
            {SIGNING_COPY.discard.action}
          </button>

          {signing.canUndo ? <PdfUndoButton controls={controls} touch /> : null}
        </span>

        <button
          type="button"
          disabled={signing.saving || !signing.hasContent}
          aria-busy={signing.saving}
          onClick={signing.save}
          className="min-h-11 rounded-md px-4 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60"
          style={{ background: token('bp-blue'), color: token('bp-on-accent') }}
        >
          {signing.saving ? SIGNING_COPY.save.working : SIGNING_COPY.save.action}
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-h-16 w-full flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <PdfPager controls={controls} touch />

      <span className="flex items-center gap-2">
        <PdfSignTools controls={controls} touch />
      </span>
    </div>
  );
}
